import React, { lazy, Suspense, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'redux/store';
import appSlice from 'redux/slices/app.slice';
import AccessLeasePacket from 'pages/public/AccessLeasePacket';
import { useGetResidencyDataQuery } from 'redux/apiSlice/residency.slice';
import {
  standartPlanProperties,
  standartPlanPropertyManagements,
} from 'data/datas/genericData';
import { oppLocalStorage, TStorageNames } from 'helpers/storage';
import useInvitedAccountLink from 'hooks/useInvitedAccountLink';
import { PublicRoutes } from './PublicRoutes';
import { PrivateRoutes } from './PrivateRoutes';
import { RoutesPaths } from './Routes.types';
import { useCurrentAuthUserAmplifyMutation } from '../redux/apiSlice/authAmplify.slice';
import { ETenantActionUI, TTenantDataUI } from '../data/types/tenant.types';
import { useGetTenantProfileQuery } from '../redux/apiSlice/tenant.slices';

const Impersonate = lazy(() => import('pages/public/Impersonation'));
const Certificate = lazy(() => import('pages/public/Certificate'));

const RoutesPage = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const amplifyUser = useAppSelector(state => state.authSlice.amplifyUser);
  // Route-scoped rather than the raw slice value: a recorded flag that outlived
  // its sign-in would otherwise skip both root queries for the life of the tab,
  // leaving the app rendering from stale localStorage with no error anywhere.
  const { pending: invitedUsername } = useInvitedAccountLink();
  // While an invited sign-in is pending, amplifyUser is still the outgoing
  // account, so these would go out signed with the outgoing session — and a
  // response landing after the switch writes the outgoing tenant back and
  // redirects into the unit they came from. They resubscribe once the login flow
  // releases the flag, by which point the arriving token is the live one.
  const skipDuringSwitch =
    !amplifyUser ||
    amplifyUser?.challengeName === 'NEW_PASSWORD_REQUIRED' ||
    !!invitedUsername;
  useGetTenantProfileQuery(null, { skip: skipDuringSwitch });
  const { data } = useGetResidencyDataQuery(null, { skip: skipDuringSwitch });

  const isBlocked =
    data?.length === 1 &&
    data?.some(
      residency =>
        standartPlanPropertyManagements.includes(
          residency.propertyManagementName,
        ) || standartPlanProperties.includes(residency.propertyName),
    );

  const isVisitationBlocked =
    data?.length === 1 &&
    data?.some(
      residency => residency.propertyManagementName === 'Urban Land Interests',
    );

  oppLocalStorage.setItem(TStorageNames.isBlocked, isBlocked);
  oppLocalStorage.setItem(
    TStorageNames.isVisitationBlocked,
    isVisitationBlocked,
  );

  const { user, isLoading } = useAppSelector(state => ({
    user: state.userSlice.user as TTenantDataUI | null,
    isLoading: state.userSlice.isLoading as boolean,
  }));

  const [reRetch] = useCurrentAuthUserAmplifyMutation();

  useEffect(() => {
    if (!user) {
      return;
    }
    reRetch(null);
  }, [user]);

  useEffect(() => {
    if (!user && location.pathname.search(/app/g) !== -1) {
      dispatch(appSlice.actions.setRedirectLink(location.pathname));
    }
  }, []);
  // Onboarding belongs to an authenticated account, so with no session there is
  // nothing to onboard. Releasing a session without a page reload otherwise turns
  // the negation below true and bounces the visitor away mid sign-in.
  const isOnboarding =
    !!amplifyUser &&
    !(
      isLoading ||
      amplifyUser?.challengeName === 'NEW_PASSWORD_REQUIRED' ||
      user?.requiredAction === ETenantActionUI.NO_ACTION
    );

  return (
    <Suspense fallback={isLoading}>
      <Routes>
        <Route
          path="/*"
          element={
            <PublicRoutes
              isOnboarding={isOnboarding}
              isLoggedIn={user?.requiredAction === ETenantActionUI.NO_ACTION}
            />
          }
        />
        <Route path="app/*" element={<PrivateRoutes isLoggedIn />} />
        <Route path={RoutesPaths.IMPERSONATION} element={<Impersonate />} />
        <Route
          path={RoutesPaths.LEASE_PACKET}
          element={<AccessLeasePacket />}
        />
        <Route
          path={RoutesPaths.LEASE_PACKET_CODE}
          element={<AccessLeasePacket />}
        />
        <Route path={RoutesPaths.CERTIFICATE} element={<Certificate />} />
      </Routes>
    </Suspense>
  );
};
export default RoutesPage;
